import { supabase } from './supabaseClient';
import { JourneyWorkflowService } from './journeyWorkflowService';
import { StepCompletionValidationService } from './stepCompletionValidationService';
import { JourneyPointsService } from './journeyPointsService';
import { JourneyManagementService } from './journeyManagementService';
import {
  Journey,
  JourneyStep,
  JourneySetup,
  CreateJourneyRequest,
  CreateJourneyResponse,
  UpdateStepStatusRequest,
  ResetJourneyRequest,
  CompletionMethod,
  JourneyRow,
  JourneyStepRow,
  journeyRowToJourney,
  journeyStepRowToJourneyStep
} from '../types/journey';

// Import cache clearing function
let clearJourneyCacheFn: (() => void) | null = null;

/**
 * Register the cache clearing function from JourneyHomeSection
 * This allows the API service to clear the cache when journey data changes
 */
export const registerCacheClearFunction = (fn: () => void) => {
  clearJourneyCacheFn = fn;
};

/**
 * Clear journey cache if the function is registered
 */
const clearCacheIfAvailable = () => {
  if (clearJourneyCacheFn) {
    clearJourneyCacheFn();
  }
};

/**
 * API service for journey management endpoints
 * Handles all database operations for startup journey workflows
 */
export class JourneyApiService {

  /**
   * POST /api/journeys/setup - Create a new journey
   * Requirements: 1, 7, 8
   */
  static async createJourney(request: CreateJourneyRequest): Promise<CreateJourneyResponse> {
    try {
      // Get current user - try both session and user for better reliability
      let user = null;
      
      // First try getting the session (works better for new signups)
      const { data: { session }, error: sessionError } = await supabase.auth.getSession();
      if (session?.user) {
        user = session.user;
      } else {
        // Fallback to getUser()
        const { data: { user: fetchedUser }, error: authError } = await supabase.auth.getUser();
        if (fetchedUser) {
          user = fetchedUser;
        }
      }
      
      if (!user) {
        console.error('Authentication failed:', sessionError);
        throw new Error('Authentication required. Please sign in and try again.');
      }

      // Validate setup data
      this.validateJourneySetup(request.setup);

      // Generate workflow using the workflow service
      const { persona, steps: generatedSteps } = JourneyWorkflowService.generateWorkflow(
        request.setup.stage,
        request.setup.market,
        request.setup.evaluation
      );

      // Deactivate any existing active journeys for this user
      const { error: deactivateError } = await supabase
        .from('journeys')
        .update({ is_active: false })
        .eq('user_id', user.id)
        .eq('is_active', true);

      if (deactivateError) {
        console.error('Error deactivating existing journeys:', deactivateError);
        // Continue anyway - this is not critical
      }

      // Create new journey record
      const journeyData = {
        user_id: user.id,
        stage: request.setup.stage,
        market: request.setup.market,
        persona,
        evaluation: request.setup.evaluation || null,
        current_step_index: 0,
        is_active: true
      };

      const { data: journeyRow, error: journeyError } = await supabase
        .from('journeys')
        .insert(journeyData)
        .select()
        .single();

      if (journeyError || !journeyRow) {
        throw new Error(`Failed to create journey: ${journeyError?.message || 'Unknown error'}`);
      }

      // Create journey steps
      const stepsData = generatedSteps.map(step => ({
        journey_id: journeyRow.id,
        step_index: step.stepIndex,
        title: step.title,
        title_zh: step.title_zh || null,
        description: step.description,
        description_zh: step.description_zh || null,
        actions: step.actions,
        dod_criteria: step.dodCriteria,
        time_estimate_weeks: step.timeEstimateWeeks,
        status: step.status
      }));

      const { data: stepsRows, error: stepsError } = await supabase
        .from('journey_steps')
        .insert(stepsData)
        .select('*, title_zh, description_zh');

      if (stepsError || !stepsRows) {
        // Rollback journey creation
        await supabase.from('journeys').delete().eq('id', journeyRow.id);
        throw new Error(`Failed to create journey steps: ${stepsError?.message || 'Unknown error'}`);
      }

      // Convert database rows to TypeScript interfaces
      const journey = journeyRowToJourney(journeyRow as JourneyRow);
      const steps = stepsRows.map(row => journeyStepRowToJourneyStep(row as JourneyStepRow));

      // Clear cache since we created a new journey
      clearCacheIfAvailable();

      return { journey, steps };

    } catch (error: any) {
      console.error('Error creating journey:', error);
      throw new Error(error.message || 'Failed to create journey');
    }
  }

  /**
   * GET /api/journeys/:id - Retrieve journey details
   * Requirements: 1, 8
   */
  static async getJourney(journeyId: string): Promise<{ journey: Journey; steps: JourneyStep[] }> {
    try {
      // Get current user
      const { data: { user }, error: authError } = await supabase.auth.getUser();
      if (authError || !user) {
        throw new Error('Authentication required');
      }

      // Validate journey ID
      if (!journeyId || typeof journeyId !== 'string') {
        throw new Error('Valid journey ID is required');
      }

      // Get journey record
      const { data: journeyRow, error: journeyError } = await supabase
        .from('journeys')
        .select('*')
        .eq('id', journeyId)
        .eq('user_id', user.id) // Ensure user can only access their own journeys
        .single();

      if (journeyError) {
        if (journeyError.code === 'PGRST116') {
          throw new Error('Journey not found');
        }
        throw new Error(`Failed to retrieve journey: ${journeyError.message}`);
      }

      if (!journeyRow) {
        throw new Error('Journey not found');
      }

      // Get journey steps
      const { data: stepsRows, error: stepsError } = await supabase
        .from('journey_steps')
        .select('*')
        .eq('journey_id', journeyId)
        .order('step_index', { ascending: true });

      if (stepsError) {
        throw new Error(`Failed to retrieve journey steps: ${stepsError.message}`);
      }

      // Convert database rows to TypeScript interfaces
      const journey = journeyRowToJourney(journeyRow as JourneyRow);
      const steps = (stepsRows || []).map(row => journeyStepRowToJourneyStep(row as JourneyStepRow));

      return { journey, steps };

    } catch (error: any) {
      console.error('Error retrieving journey:', error);
      throw new Error(error.message || 'Failed to retrieve journey');
    }
  }

  /**
   * PUT /api/journeys/:id/reset - Reset journey functionality with history tracking
   * Requirements: 7
   */
  static async resetJourney(request: ResetJourneyRequest): Promise<CreateJourneyResponse & {
    historyEntry: any;
  }> {
    try {
      // Use the enhanced reset functionality from JourneyManagementService
      const result = await JourneyManagementService.resetJourneyWithHistory(request);
      
      // Clear cache since journey was reset
      clearCacheIfAvailable();
      
      return {
        journey: result.newJourney,
        steps: result.newSteps,
        historyEntry: result.historyEntry
      };

    } catch (error: any) {
      console.error('Error resetting journey:', error);
      throw new Error(error.message || 'Failed to reset journey');
    }
  }

  /**
   * PUT /api/journeys/steps/:stepId/status - Update step status (completion/skipping)
   * Requirements: 3, 7, 8
   */
  static async updateStepStatus(request: UpdateStepStatusRequest): Promise<{ 
    step: JourneyStep; 
    pointsAwarded: number;
    nextStepUnlocked?: boolean;
  }> {
    try {
      // Get current user
      const { data: { user }, error: authError } = await supabase.auth.getUser();
      if (authError || !user) {
        throw new Error('Authentication required');
      }

      // Validate request
      if (!request.stepId || typeof request.stepId !== 'string') {
        throw new Error('Valid step ID is required');
      }

      if (!['completed', 'skipped'].includes(request.status)) {
        throw new Error('Status must be either "completed" or "skipped"');
      }

      // Get step and verify ownership
      const { data: stepRow, error: stepError } = await supabase
        .from('journey_steps')
        .select(`
          *,
          journeys!inner(user_id, current_step_index, is_active)
        `)
        .eq('id', request.stepId)
        .single();

      if (stepError || !stepRow) {
        throw new Error('Step not found');
      }

      // Type assertion for the joined data
      const stepWithJourney = stepRow as JourneyStepRow & {
        journeys: { user_id: string; current_step_index: number; is_active: boolean };
      };

      // Verify user ownership and journey is active
      if (stepWithJourney.journeys.user_id !== user.id) {
        throw new Error('Access denied');
      }

      if (!stepWithJourney.journeys.is_active) {
        throw new Error('Cannot update steps in inactive journey');
      }

      // Verify step can be updated (must be current step or already completed/skipped)
      if (stepWithJourney.status === 'locked') {
        throw new Error('Cannot update locked step');
      }

      // Calculate points awarded
      let pointsAwarded = 0;
      if (request.status === 'completed') {
        // Award points based on step actions
        pointsAwarded = stepWithJourney.actions.reduce((total, action) => total + action.pointsReward, 0);
        
        // Add reflection bonus if reflection notes provided
        if (request.reflectionNotes && request.reflectionNotes.trim().length > 0) {
          pointsAwarded += 3;
        }
      }

      // Update step status
      const updateData: Partial<JourneyStepRow> = {
        status: request.status,
        completed_at: request.status === 'completed' ? new Date().toISOString() : undefined
      };

      const { data: updatedStepRow, error: updateError } = await supabase
        .from('journey_steps')
        .update(updateData)
        .eq('id', request.stepId)
        .select()
        .single();

      if (updateError || !updatedStepRow) {
        throw new Error(`Failed to update step: ${updateError?.message || 'Unknown error'}`);
      }

      // Record step completion
      const completionData = {
        step_id: request.stepId,
        user_id: user.id,
        completion_method: request.status === 'completed' ? 'manual' as CompletionMethod : 'skipped' as CompletionMethod,
        reflection_notes: request.reflectionNotes || null,
        points_awarded: pointsAwarded
      };

      const { error: completionError } = await supabase
        .from('step_completions')
        .insert(completionData);

      if (completionError) {
        console.error('Error recording step completion:', completionError);
        // Continue anyway - this is not critical for the main flow
      }

      // Award points to user using the journey points service
      if (pointsAwarded > 0) {
        try {
          const step = journeyStepRowToJourneyStep(stepWithJourney);
          const hasReflection = !!(request.reflectionNotes && request.reflectionNotes.trim().length > 0);
          
          await JourneyPointsService.awardStepCompletionBonus(
            user.id, 
            step, 
            hasReflection
          );
        } catch (pointsError) {
          console.error('Error awarding points:', pointsError);
          // Continue anyway - step completion is more important than points
        }
      }

      // Check if next step should be unlocked
      let nextStepUnlocked = false;
      if (request.status === 'completed' || request.status === 'skipped') {
        const nextStepIndex = stepWithJourney.step_index + 1;
        
        // Get next step
        const { data: nextStepRow, error: nextStepError } = await supabase
          .from('journey_steps')
          .select('id, status')
          .eq('journey_id', stepWithJourney.journey_id)
          .eq('step_index', nextStepIndex)
          .single();

        if (!nextStepError && nextStepRow && nextStepRow.status === 'locked') {
          // Unlock next step
          const { error: unlockError } = await supabase
            .from('journey_steps')
            .update({ status: 'current' })
            .eq('id', nextStepRow.id);

          if (!unlockError) {
            nextStepUnlocked = true;
            
            // Update journey's current step index
            const { error: journeyUpdateError } = await supabase
              .from('journeys')
              .update({ current_step_index: nextStepIndex })
              .eq('id', stepWithJourney.journey_id);

            if (journeyUpdateError) {
              console.error('Error updating journey current step:', journeyUpdateError);
            }
          }
        }
      }

      // Convert updated step to TypeScript interface
      const updatedStep = journeyStepRowToJourneyStep(updatedStepRow as JourneyStepRow);

      // Clear cache since step status changed
      clearCacheIfAvailable();

      return {
        step: updatedStep,
        pointsAwarded,
        nextStepUnlocked
      };

    } catch (error: any) {
      console.error('Error updating step status:', error);
      throw new Error(error.message || 'Failed to update step status');
    }
  }

  /**
   * GET /api/journeys/user/active - Get user's active journey
   * Requirements: 8
   * OPTIMIZED: Single query with joined steps for better performance
   */
  static async getUserActiveJourney(): Promise<{ journey: Journey; steps: JourneyStep[] } | null> {
    try {
      // Get current user
      const { data: { user }, error: authError } = await supabase.auth.getUser();
      if (authError || !user) {
        throw new Error('Authentication required');
      }

      // OPTIMIZED: Get active journey with steps in a single query using join
      const { data: journeyRow, error: journeyError } = await supabase
        .from('journeys')
        .select(`
          *,
          journey_steps (*)
        `)
        .eq('user_id', user.id)
        .eq('is_active', true)
        .order('created_at', { ascending: false })
        .limit(1)
        .single();

      if (journeyError) {
        if (journeyError.code === 'PGRST116') {
          // No active journey found
          return null;
        }
        throw new Error(`Failed to retrieve active journey: ${journeyError.message}`);
      }

      if (!journeyRow) {
        return null;
      }

      // Extract steps from the joined data
      const stepsRows = (journeyRow as any).journey_steps || [];
      
      // Sort steps by step_index
      stepsRows.sort((a: any, b: any) => a.step_index - b.step_index);

      // Convert database rows to TypeScript interfaces
      const journey = journeyRowToJourney(journeyRow as JourneyRow);
      const steps = stepsRows.map((row: any) => journeyStepRowToJourneyStep(row as JourneyStepRow));

      return { journey, steps };

    } catch (error: any) {
      console.error('Error retrieving active journey:', error);
      throw new Error(error.message || 'Failed to retrieve active journey');
    }
  }

  /**
   * GET /api/journeys/user/all - Get all user journeys
   * Requirements: 8
   */
  static async getUserAllJourneys(): Promise<Journey[]> {
    try {
      // Get current user
      const { data: { user }, error: authError } = await supabase.auth.getUser();
      if (authError || !user) {
        throw new Error('Authentication required');
      }

      // Get all journeys for the user (not just active ones)
      const { data: journeyRows, error: journeyError } = await supabase
        .from('journeys')
        .select(`
          *,
          journey_steps(id, status)
        `)
        .eq('user_id', user.id)
        .order('created_at', { ascending: false }); // Most recent first

      if (journeyError) {
        throw new Error(`Failed to retrieve user journeys: ${journeyError.message}`);
      }

      if (!journeyRows) {
        return [];
      }

      // Convert to TypeScript interfaces and add progress information
      return journeyRows.map(row => {
        const journey = journeyRowToJourney(row as JourneyRow);
        
        // Calculate progress from steps
        const steps = row.journey_steps || [];
        const completedSteps = steps.filter((s: any) => s.status === 'completed' || s.status === 'skipped').length;
        const totalSteps = steps.length;
        
        // Add progress metadata
        (journey as any).progress = {
          completed: completedSteps,
          total: totalSteps,
          percentage: totalSteps > 0 ? Math.round((completedSteps / totalSteps) * 100) : 0
        };
        
        return journey;
      });

    } catch (error: any) {
      console.error('Error retrieving all user journeys:', error);
      throw new Error(error.message || 'Failed to retrieve user journeys');
    }
  }

  /**
   * PUT /api/journeys/steps/:stepId/skip - Skip a step with justification
   * Requirements: 7
   */
  static async skipStep(stepId: string, justification?: string, skipType: 'already_done' | 'not_applicable' | 'custom' = 'custom'): Promise<{
    step: JourneyStep;
    justification: any;
    nextStepUnlocked?: boolean;
  }> {
    if (!justification || justification.trim().length === 0) {
      throw new Error('Justification is required for skipping steps');
    }

    // Use the enhanced skip functionality from JourneyManagementService
    return await JourneyManagementService.skipStepWithJustification({
      stepId,
      justification: justification.trim(),
      skipType
    });
  }

  /**
   * PUT /api/journeys/steps/:stepId/complete - Complete a step with validation
   * Requirements: 3, 7, 8
   */
  static async completeStepWithValidation(
    stepId: string,
    method: CompletionMethod,
    reflectionNotes?: string
  ): Promise<{
    step: JourneyStep;
    pointsAwarded: number;
    nextStepUnlocked?: boolean;
    validationResults: any;
  }> {
    try {
      // Get current user
      const { data: { user }, error: authError } = await supabase.auth.getUser();
      if (authError || !user) {
        throw new Error('Authentication required');
      }

      // Record completion using validation service
      const completionResult = await StepCompletionValidationService.recordStepCompletion(
        stepId,
        method,
        reflectionNotes
      );

      // Update step status in database
      const { data: updatedStepRow, error: updateError } = await supabase
        .from('journey_steps')
        .update({
          status: 'completed',
          completed_at: new Date().toISOString()
        })
        .eq('id', stepId)
        .select(`
          *,
          journeys!inner(id, current_step_index)
        `)
        .single();

      if (updateError || !updatedStepRow) {
        throw new Error(`Failed to update step: ${updateError?.message || 'Unknown error'}`);
      }

      // Check if next step should be unlocked
      let nextStepUnlocked = false;
      const stepWithJourney = updatedStepRow as any;
      const nextStepIndex = stepWithJourney.step_index + 1;
      
      // Get next step
      const { data: nextStepRow, error: nextStepError } = await supabase
        .from('journey_steps')
        .select('id, status')
        .eq('journey_id', stepWithJourney.journey_id)
        .eq('step_index', nextStepIndex)
        .single();

      if (!nextStepError && nextStepRow && nextStepRow.status === 'locked') {
        // Unlock next step
        const { error: unlockError } = await supabase
          .from('journey_steps')
          .update({ status: 'current' })
          .eq('id', nextStepRow.id);

        if (!unlockError) {
          nextStepUnlocked = true;
          
          // Update journey's current step index
          const { error: journeyUpdateError } = await supabase
            .from('journeys')
            .update({ current_step_index: nextStepIndex })
            .eq('id', stepWithJourney.journey_id);

          if (journeyUpdateError) {
            console.error('Error updating journey current step:', journeyUpdateError);
          }
        }
      }

      // Convert updated step to TypeScript interface
      const updatedStep = journeyStepRowToJourneyStep(updatedStepRow as JourneyStepRow);

      // Clear cache since step was completed
      clearCacheIfAvailable();

      return {
        step: updatedStep,
        pointsAwarded: completionResult.pointsAwarded,
        nextStepUnlocked,
        validationResults: completionResult.validationResults
      };

    } catch (error: any) {
      console.error('Error completing step with validation:', error);
      throw new Error(error.message || 'Failed to complete step');
    }
  }

  /**
   * Private helper methods
   */

  /**
   * Validates journey setup data
   */
  private static validateJourneySetup(setup: JourneySetup): void {
    if (!setup.stage || !['Beginner', 'Have Idea', 'In Progress'].includes(setup.stage)) {
      throw new Error('Valid stage is required');
    }

    if (!setup.market || !['IT/Tech', 'E-commerce', 'Social Media/Influencer', 'Other'].includes(setup.market)) {
      throw new Error('Valid market is required');
    }

    if (setup.evaluation) {
      const factors = ['idea', 'skills', 'team', 'funding', 'market', 'tech', 'network', 'time'];
      for (const factor of factors) {
        const value = setup.evaluation[factor as keyof typeof setup.evaluation];
        if (typeof value !== 'number' || value < 0 || value > 3) {
          throw new Error(`Evaluation factor '${factor}' must be a number between 0 and 3`);
        }
      }
    }
  }

  /**
   * Check if user has sufficient points for an action
   */
  static async checkActionAffordability(userId: string, pointsCost: number): Promise<{
    canAfford: boolean;
    currentPoints: number;
    shortfall?: number;
    suggestions?: any;
  }> {
    try {
      const pointsCheck = await JourneyPointsService.checkSufficientPoints(userId, pointsCost);
      
      let suggestions;
      if (!pointsCheck.sufficient && pointsCheck.shortfall) {
        suggestions = JourneyPointsService.getEarningSuggestions(pointsCheck.shortfall);
      }

      return {
        canAfford: pointsCheck.sufficient,
        currentPoints: pointsCheck.currentPoints,
        shortfall: pointsCheck.shortfall,
        suggestions
      };
    } catch (error) {
      console.error('Error checking action affordability:', error);
      return {
        canAfford: false,
        currentPoints: 0,
        shortfall: pointsCost
      };
    }
  }

  /**
   * Process action cost deduction
   */
  static async processActionCost(userId: string, actionType: string, pointsCost: number, stepTitle: string): Promise<{
    success: boolean;
    error?: any;
  }> {
    try {
      if (pointsCost <= 0) {
        return { success: true };
      }

      // Create a mock action for the points service
      const mockAction = {
        text: actionType,
        type: actionType as any,
        templateId: '',
        pointsCost,
        pointsReward: 0
      };

      return await JourneyPointsService.deductActionCost(userId, mockAction, stepTitle);
    } catch (error) {
      console.error('Error processing action cost:', error);
      return { success: false, error };
    }
  }

  /**
   * Get journey points summary
   */
  static async getJourneyPointsSummary(journeyId: string): Promise<{
    summary: any;
    milestones: any;
    userPoints: number;
  }> {
    try {
      // Get current user
      const { data: { user }, error: authError } = await supabase.auth.getUser();
      if (authError || !user) {
        throw new Error('Authentication required');
      }

      // Get journey with steps
      const { journey, steps } = await this.getJourney(journeyId);
      
      // Calculate points summary
      const summary = JourneyPointsService.calculateJourneyPointsSummary(steps);
      
      // Check milestone eligibility
      const milestones = JourneyPointsService.checkMilestoneEligibility(steps);
      
      // Get user's current points
      const pointsCheck = await JourneyPointsService.checkSufficientPoints(user.id, 0);

      return {
        summary,
        milestones,
        userPoints: pointsCheck.currentPoints
      };
    } catch (error: any) {
      console.error('Error getting journey points summary:', error);
      throw new Error(error.message || 'Failed to get journey points summary');
    }
  }

  /**
   * GET /api/journeys/history - Get user's journey history
   * Requirements: 7
   */
  static async getUserJourneyHistory(limit: number = 10): Promise<any[]> {
    try {
      return await JourneyManagementService.getUserJourneyHistory(undefined, limit);
    } catch (error: any) {
      console.error('Error getting user journey history:', error);
      throw new Error(error.message || 'Failed to retrieve journey history');
    }
  }

  /**
   * GET /api/journeys/user/experience - Check if user is experienced
   * Requirements: 7
   */
  static async checkUserExperience(): Promise<{
    isExperienced: boolean;
    canCustomize: boolean;
    completedJourneys: number;
    totalResets: number;
  }> {
    try {
      // Get current user
      const { data: { user }, error: authError } = await supabase.auth.getUser();
      if (authError || !user) {
        throw new Error('Authentication required');
      }

      const isExperienced = await JourneyManagementService.checkIfUserIsExperienced(user.id);

      // Get additional stats
      const { data: journeyStats, error: statsError } = await supabase
        .from('journeys')
        .select('id, reset_count')
        .eq('user_id', user.id);

      let completedJourneys = 0;
      let totalResets = 0;

      if (!statsError && journeyStats) {
        totalResets = journeyStats.reduce((sum, j) => sum + (j.reset_count || 0), 0);
        
        // Count completed journeys (journeys with all steps completed)
        for (const journey of journeyStats) {
          const { data: steps, error: stepsError } = await supabase
            .from('journey_steps')
            .select('status')
            .eq('journey_id', journey.id);

          if (!stepsError && steps && steps.length > 0) {
            const allCompleted = steps.every(step => 
              step.status === 'completed' || step.status === 'skipped'
            );
            if (allCompleted) {
              completedJourneys++;
            }
          }
        }
      }

      return {
        isExperienced,
        canCustomize: isExperienced,
        completedJourneys,
        totalResets
      };

    } catch (error: any) {
      console.error('Error checking user experience:', error);
      throw new Error(error.message || 'Failed to check user experience');
    }
  }

  /**
   * GET /api/journeys/:id/customizations - Get journey customizations
   * Requirements: 7
   */
  static async getJourneyCustomizations(journeyId: string): Promise<any[]> {
    try {
      return await JourneyManagementService.getJourneyCustomizations(journeyId);
    } catch (error: any) {
      console.error('Error getting journey customizations:', error);
      throw new Error(error.message || 'Failed to retrieve customizations');
    }
  }

  /**
   * PUT /api/journeys/:id/customize - Apply journey customizations
   * Requirements: 7
   */
  static async applyJourneyCustomizations(journeyId: string, customizations: any[]): Promise<{
    journey: Journey;
    steps: JourneyStep[];
    customizations: any[];
  }> {
    try {
      return await JourneyManagementService.applyJourneyCustomizations({
        journeyId,
        customizations
      });
    } catch (error: any) {
      console.error('Error applying journey customizations:', error);
      throw new Error(error.message || 'Failed to apply customizations');
    }
  }

  /**
   * GET /api/journeys/:id/skip-justifications - Get step skip justifications
   * Requirements: 7
   */
  static async getStepSkipJustifications(journeyId: string): Promise<any[]> {
    try {
      return await JourneyManagementService.getStepSkipJustifications(journeyId);
    } catch (error: any) {
      console.error('Error getting step skip justifications:', error);
      throw new Error(error.message || 'Failed to retrieve skip justifications');
    }
  }
}